import React, { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';
import { Flashbar, FlashbarProps } from '@cloudscape-design/components';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: string;
  type: NotificationType;
  header: string;
  content?: string;
  dismissible?: boolean;
  dismissLabel?: string;
  onDismiss?: () => void;
}

interface NotificationContextType {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  showSuccess: (header: string, content?: string) => void;
  showError: (header: string, content?: string) => void;
  showWarning: (header: string, content?: string) => void;
  showInfo: (header: string, content?: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

/**
 * Notification Provider Component
 * 
 * Provides notification management functionality throughout the app.
 * Wraps the app to enable notifications from any component.
 * 
 * Usage:
 * <NotificationProvider>
 *   <App />
 * </NotificationProvider>
 */
export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const removeNotification = useCallback((id: string) => {
    const timerId = timersRef.current.get(id);
    if (timerId) {
      clearTimeout(timerId);
      timersRef.current.delete(id);
    }
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const addNotification = useCallback((notification: Omit<Notification, 'id'>) => {
    const id = `notification-${Date.now()}-${Math.random()}`;
    const newNotification: Notification = {
      ...notification,
      id,
      dismissible: notification.dismissible ?? true,
      dismissLabel: notification.dismissLabel ?? 'Dismiss',
    };

    setNotifications((prev) => [...prev, newNotification]);

    // Auto-dismiss after 10 seconds for success/info messages
    if (notification.type === 'success' || notification.type === 'info') {
      const timerId = setTimeout(() => {
        removeNotification(id);
      }, 10000);
      timersRef.current.set(id, timerId);
    }
  }, [removeNotification]);

  const clearNotifications = useCallback(() => {
    timersRef.current.forEach(t => clearTimeout(t));
    timersRef.current.clear();
    setNotifications([]);
  }, []);

  useEffect(() => {
    return () => {
      timersRef.current.forEach(t => clearTimeout(t));
    };
  }, []);

  const showSuccess = useCallback((header: string, content?: string) => {
    addNotification({ type: 'success', header, content });
  }, [addNotification]);

  const showError = useCallback((header: string, content?: string) => {
    addNotification({ type: 'error', header, content });
  }, [addNotification]);

  const showWarning = useCallback((header: string, content?: string) => {
    addNotification({ type: 'warning', header, content });
  }, [addNotification]);

  const showInfo = useCallback((header: string, content?: string) => {
    addNotification({ type: 'info', header, content });
  }, [addNotification]);

  const value: NotificationContextType = {
    notifications,
    addNotification,
    removeNotification,
    clearNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

/**
 * Hook to access notification functionality
 * 
 * Usage:
 * const { showSuccess, showError } = useNotifications();
 * showSuccess('Program created', 'Your program has been created successfully');
 * showError('Failed to create program', error.message);
 */
export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

/**
 * Flashbar Component
 * 
 * Displays notifications at the top of the page.
 * Should be placed near the top of your layout.
 * 
 * Usage:
 * <NotificationFlashbar />
 */
export const NotificationFlashbar: React.FC = () => {
  const { notifications, removeNotification } = useNotifications();

  const flashbarItems: FlashbarProps.MessageDefinition[] = notifications.map((notification) => ({
    type: notification.type,
    header: notification.header,
    content: notification.content,
    dismissible: notification.dismissible,
    dismissLabel: notification.dismissLabel,
    onDismiss: () => {
      removeNotification(notification.id);
      notification.onDismiss?.();
    },
    id: notification.id,
  }));

  return <Flashbar items={flashbarItems} />;
};
